import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import axios from 'axios';
import * as XLSX from 'xlsx';
import DetailModal from '../components/DetailModal';
import './HotelBookings.css';

interface HotelBooking {
  hotel_order_id?: number;
  booking_id?: number;
  id?: number;
  status: string;
  hotel_name?: string;
  total_price?: number;
  check_in_date?: string;
  check_out_date?: string;
  created_at?: string;
  reference_number?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  nights?: number;
  guests?: number;
  room_type?: string;
}

// Detailed structure from API response (parsed from details JSON)
interface HotelOrderDetail {
  booking_id: number;
  booking_ref_no: string;
  confirmation_no: string;
  created_at: string;
  currency: string;
  details: string; // JSON string
  email: string;
  first_name: string;
  last_name: string;
  hotel_name?: string;  // added
  invoice_pdf: string | null;
  status: string;
  tbo_booking_id: number;
  ticket_pdf: string | null;
  total_price: string;
  user_id: number;
}

interface ParsedDetails {
  GetBookingDetailResult: {
    BookingId: number;
    BookingRefNo: string;
    ConfirmationNo: string;
    HotelName: string;
    CheckInDate: string;
    CheckOutDate: string;
    NoOfRooms: number;
    Rooms: Array<{
      RoomTypeName: string;
      AdultCount: number;
      ChildCount: number;
      Inclusion: string;
      PriceBreakUp: {
        RoomRate: number;
        RoomTax: number;
        CurrencyCode: string;
      };
      CancelPolicies?: Array<{
        FromDate: string;
        ToDate: string;
        CancellationCharge: number;
        ChargeType: number;
      }>;
      CancellationPolicy?: string;
      HotelPassenger: Array<{
        Title: string;
        FirstName: string;
        LastName: string;
        Age: number;
        PaxType: number;
        Email: string;
        Phoneno: string;
        LeadPassenger: boolean;
      }>;
    }>;
    RateConditions?: string[];
    NetAmount: number;
    NetTax: number;
    CurrencyCode: string;
    CancellationPolicy?: string;
    HotelAddress?: string;
    AddressLine1?: string;
    AddressLine2?: string;
    City?: string;
    CountryCode?: string;
  };
}

const HotelBookings: React.FC = () => {
  const [bookings, setBookings] = useState<HotelBooking[]>([]);
  const [filteredBookings, setFilteredBookings] = useState<HotelBooking[]>([]);
  const [invoiceMap, setInvoiceMap] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState<HotelOrderDetail | null>(null);
  const [modalBooking, setModalBooking] = useState<HotelBooking | null>(null);
  const [parsedDetails, setParsedDetails] = useState<ParsedDetails['GetBookingDetailResult'] | null>(null);

  useEffect(() => {
    loadBookings();
  }, []);

  useEffect(() => {
    filterAndSort();
  }, [bookings, searchTerm, statusFilter, sortBy]);

  const loadBookings = async () => {
    setLoading(true);
    try {
      const res = await api.getHotelOrders(1, 100);
      const orders = res.hotel_orders || [];
      console.log('Hotel orders:', orders);

      // Enhance each booking by parsing the details JSON to extract hotel_name, dates, etc.
      const enhancedOrders = orders.map((order: any) => {
        const enhanced = { ...order };
        if (order.details && order.details.booking_details_json) {
          try {
            const parsed = JSON.parse(order.details.booking_details_json);
            const result = parsed.GetBookingDetailResult || parsed;
            enhanced.hotel_name = result.HotelName || enhanced.hotel_name;
            enhanced.check_in_date = result.CheckInDate || enhanced.check_in_date;
            enhanced.check_out_date = result.CheckOutDate || enhanced.check_out_date;
            enhanced.nights = result.Nights || (result.CheckInDate && result.CheckOutDate
              ? Math.ceil((new Date(result.CheckOutDate).getTime() - new Date(result.CheckInDate).getTime()) / (1000 * 60 * 60 * 24))
              : enhanced.nights);
            enhanced.guests = result.NoOfRooms ? result.NoOfRooms * (result.Rooms?.[0]?.AdultCount || 1) : enhanced.guests;
            enhanced.room_type = result.Rooms?.[0]?.RoomTypeName;
          } catch (e) {
            console.warn('Failed to parse booking details for order', order.booking_id, e);
          }
        }
        return enhanced;
      });

      setBookings(enhancedOrders);
      setFilteredBookings(enhancedOrders);

      // Load invoices map
      try {
        const invResp = await api.getInvoicesFromServer(1, 500);
        console.log('Invoices response:', invResp);
        const map: Record<string, any> = {};
        if (invResp?.invoices) {
          invResp.invoices.forEach((inv: any) => {
            const type = inv.type || 'hotel';
            const bid = inv.booking_id ?? inv.bookingId ?? inv.id ?? inv.order_id;
            if (type && bid) {
              map[`${type}-${bid}`] = inv;
            }
          });
        }
        console.log('Invoice map:', map);
        setInvoiceMap(map);
      } catch (e) {
        console.warn('Invoice load failed', e);
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load hotel bookings');
    } finally {
      setLoading(false);
    }
  };

  const filterAndSort = () => {
    let filtered = [...bookings];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(b =>
        b.hotel_name?.toLowerCase().includes(term) ||
        b.reference_number?.toLowerCase().includes(term) ||
        b.first_name?.toLowerCase().includes(term) ||
        b.last_name?.toLowerCase().includes(term) ||
        b.email?.toLowerCase().includes(term)
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(b => b.status?.toLowerCase() === statusFilter.toLowerCase());
    }

    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'created':
          return new Date(b.created_at || b.check_in_date || 0).getTime() - new Date(a.created_at || a.check_in_date || 0).getTime();
        case 'amount':
          return (b.total_price || 0) - (a.total_price || 0);
        case 'hotel':
          return (a.hotel_name || '').localeCompare(b.hotel_name || '');
        default:
          return 0;
      }
    });

    setFilteredBookings(filtered);
  };

  const handleView = async (booking: HotelBooking) => {
    try {
      const id = booking.hotel_order_id || booking.booking_id || booking.id;
      if (!id) {
        alert('Invalid booking ID');
        return;
      }
      const res = await api.getHotelOrderDetail(id);
      const data = res?.hotel_order || res;
      setModalData(data);
      setModalBooking(booking);

      // Parse the details JSON
      if (data?.details) {
        try {
          const parsed = JSON.parse(data.details);
          setParsedDetails(parsed.GetBookingDetailResult || parsed);
        } catch (e) {
          console.warn('Failed to parse hotel details JSON', e);
          setParsedDetails(null);
        }
      } else {
        setParsedDetails(null);
      }

      setModalOpen(true);
    } catch (err) {
      alert('Failed to fetch details');
    }
  };

  const handleDownloadInvoice = async (booking: HotelBooking) => {
    try {
      const id = booking.hotel_order_id || booking.booking_id || booking.id;
      if (!id) {
        alert('Invalid booking ID');
        return;
      }
      const { url, headers } = await api.downloadInvoice('hotel', id);
      const resp = await axios.get(url, { headers, responseType: 'blob' });
      const blob = new Blob([resp.data], { type: resp.headers['content-type'] || 'application/pdf' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `hotel_invoice_${id}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      alert('Invoice download failed');
    }
  };

  const getStatusClass = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'confirmed':
        return 'badge-success';
      case 'pending':
        return 'badge-warning';
      case 'cancelled':
        return 'badge-danger';
      default:
        return 'badge-secondary';
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const formatDateShort = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };
  const handleExcelExport = () => {
    const rows = filteredBookings.map((b) => ({
      'Booking ID': b.hotel_order_id || b.booking_id || b.id || '',
      'Reference No': b.reference_number || '',
      'Hotel Name': b.hotel_name || '',
      'Status': b.status || '',
      'Guest Name': `${b.first_name || ''} ${b.last_name || ''}`.trim(),
      'Email': b.email || '',
      'Phone': b.phone || '',
      'Check-In': b.check_in_date ? new Date(b.check_in_date).toLocaleDateString() : '',
      'Check-Out': b.check_out_date ? new Date(b.check_out_date).toLocaleDateString() : '',
      'Nights': b.nights || '',
      'Guests': b.guests || '',
      'Room Type': b.room_type || '',
      'Total Price': Number(b.total_price) || 0,
      'Created At': b.created_at ? new Date(b.created_at).toLocaleString() : '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Hotel Bookings');
    XLSX.writeFile(wb, 'hotel_bookings.xlsx');
  };

const handleTallyExport = async () => {
  try {
    const params = new URLSearchParams();
    if (searchTerm) params.append('search', searchTerm);
    if (statusFilter !== 'all') params.append('status', statusFilter);
    // Optional: add date range if you add date pickers later
    const response = await api.exportHotelBookingsTally(params);
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'hotel_bookings_tally.xml');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (err: any) {
    if (err.response?.status === 404) alert('No hotel bookings to export');
    else alert('Export failed. Please try again.');
    console.error(err);
  }
};
  // Helper to get full address
  const getFullAddress = (parsed: any) => {
    const parts = [
      parsed.AddressLine1,
      parsed.AddressLine2,
      parsed.City,
      parsed.CountryCode
    ].filter(p => p && p.trim() !== '');
    return parts.length ? parts.join(', ') : 'N/A';
  };

  // Helper to get cancellation policy text
  const getCancellationPolicy = (parsed: any): string => {
    if (!parsed) return 'N/A';
    if (parsed.CancellationPolicy) return parsed.CancellationPolicy;
    if (parsed.Rooms && parsed.Rooms[0] && parsed.Rooms[0].CancellationPolicy) {
      return parsed.Rooms[0].CancellationPolicy;
    }
    if (parsed.Rooms && parsed.Rooms[0] && parsed.Rooms[0].CancelPolicies) {
      const policies = parsed.Rooms[0].CancelPolicies;
      return policies.map((p: any) => 
        `From ${p.FromDate} to ${p.ToDate}: ${p.CancellationCharge}% charge`
      ).join('; ');
    }
    return 'Not specified';
  };
  

  return (
    <div className="hotel-bookings-page">
      <div className="page-header">
      <div>
        <h1>Hotel Bookings</h1>
        <p>Manage all hotel reservations</p>
      </div>
    <div style={{ display: 'flex', gap: '12px' }}>
      <button className="btn-primary" onClick={handleTallyExport}>
        📥 Download for Tally
      </button>
      <button className="btn-primary" onClick={handleExcelExport}>
        📊 Download Excel
      </button>
    </div>
  </div>

      <div className="filters-bar">
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Search by hotel, guest, reference..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All Status</option>
            <option value="confirmed">Confirmed</option>
            <option value="pending">Pending</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="created">Newest</option>
            <option value="amount">Amount</option>
            <option value="hotel">Hotel Name</option>
          </select>
        </div>
      </div>

      <div className="stats-mini">
        <div className="stat-mini-item">
          <span className="stat-mini-label">Total</span>
          <span className="stat-mini-value">{filteredBookings.length}</span>
        </div>
        <div className="stat-mini-item">
          <span className="stat-mini-label">Revenue</span>
          <span className="stat-mini-value">
           ₹{filteredBookings
  .reduce((sum, b) => sum + Number(b.total_price || 0), 0)
  .toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}
          </span>
        </div>
        <div className="stat-mini-item">
          <span className="stat-mini-label">Confirmed</span>
          <span className="stat-mini-value">
            {filteredBookings.filter(b => b.status?.toLowerCase() === 'confirmed').length}
          </span>
        </div>
      </div>

      {loading && (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading hotel bookings...</p>
        </div>
      )}

      {error && (
        <div className="error-state">
          <span className="error-icon">⚠️</span>
          <p>{error}</p>
          <button onClick={loadBookings} className="btn-outline">Retry</button>
        </div>
      )}

      {!loading && !error && (
        <div className="bookings-grid">
          {filteredBookings.map((booking) => {
            const bookingId = booking.hotel_order_id || booking.booking_id || booking.id;
            const invoiceKey = `hotel-${bookingId}`;
            const hasInvoice = !!invoiceMap[invoiceKey];
            const guestName = booking.first_name && booking.last_name
              ? `${booking.first_name} ${booking.last_name}`
              : booking.email || 'Unknown';

            return (
              <div key={bookingId} className="booking-card">
                <div className="card-header">
                  <div className="booking-type">
                    <span className="type-icon">🏨</span>
                    <span className="type-label">HOTEL</span>
                    <span className="booking-id-label">#{bookingId}</span>
                  </div>
                  <span className={`status-badge ${getStatusClass(booking.status)}`}>
                    {booking.status || 'N/A'}
                  </span>
                </div>

                <div className="card-body">
                  <div className="hotel-name-large">{booking.hotel_name || 'Unknown Hotel'}</div>
                  
                  {booking.reference_number && (
                    <div className="ref-box">
                      <span className="ref-label">Ref:</span>
                      <span className="ref-value">{booking.reference_number}</span>
                    </div>
                  )}

                  <div className="stay-details">
                    {booking.check_in_date && (
                      <div className="check-dates">
                        <span>📅 {new Date(booking.check_in_date).toLocaleDateString()}</span>
                        {booking.check_out_date && (
                          <> → {new Date(booking.check_out_date).toLocaleDateString()}</>
                        )}
                      </div>
                    )}
                    {booking.nights && (
                      <div className="nights">{booking.nights} night{booking.nights > 1 ? 's' : ''}</div>
                    )}
                    {booking.guests && (
                      <div className="guests">{booking.guests} guest{booking.guests > 1 ? 's' : ''}</div>
                    )}
                  </div>

                  {booking.room_type && (
                    <div className="room-type">Room: {booking.room_type}</div>
                  )}

                  <div className="guest-info">
                    <div className="user-avatar">{guestName.charAt(0)}</div>
                    <div className="guest-details">
                      <div className="guest-name">{guestName}</div>
                      {booking.email && <div className="guest-email">{booking.email}</div>}
                    </div>
                  </div>

                  <div className="booking-meta">
                    <div className="meta-date">
                      <span className="meta-icon">📅</span>
                      <span>{new Date(booking.created_at || booking.check_in_date || '').toLocaleDateString()}</span>
                    </div>
                    <div className="meta-amount">₹{(booking.total_price || 0).toLocaleString()}</div>
                  </div>
                </div>

                <div className="card-footer">
                  <button className="btn-view" onClick={() => handleView(booking)}>View</button>
                  <div className="action-buttons">
                    {hasInvoice && (
                      <button className="btn-icon" onClick={() => handleDownloadInvoice(booking)} title="Download Invoice">📄</button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {filteredBookings.length === 0 && (
            <div className="empty-state">
              <span className="empty-icon">📭</span>
              <h3>No hotel bookings found</h3>
              <p>Try adjusting your filters</p>
            </div>
          )}
        </div>
      )}

      {/* Enhanced Detail Modal */}
      <DetailModal open={modalOpen} onClose={() => setModalOpen(false)} title="Hotel Booking Details">
        {modalData && parsedDetails ? (
          <div className="flight-detail-modal">
            {/* Action Buttons */}
            <div className="modal-actions">
              {modalBooking && invoiceMap[`hotel-${modalBooking.hotel_order_id || modalBooking.booking_id || modalBooking.id}`] && (
                <button className="btn-primary" onClick={() => handleDownloadInvoice(modalBooking)}>
                  Download Invoice
                </button>
              )}
            </div>

            {/* Booking Information */}
            <div className="detail-section">
              <h3 className="section-title">Booking Information</h3>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Booking ID</span>
                  <span className="info-value">#{parsedDetails.BookingId || modalData.booking_id}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Booking Ref</span>
                  <span className="info-value">{parsedDetails.BookingRefNo || modalData.booking_ref_no || '-'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Confirmation No</span>
                  <span className="info-value">{parsedDetails.ConfirmationNo || modalData.confirmation_no || '-'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Hotel Name</span>
                  <span className="info-value">{parsedDetails.HotelName || modalData.hotel_name}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Status</span>
                  <span className={`status-badge ${getStatusClass(modalData.status)}`}>
                    {modalData.status}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Check-In</span>
                  <span className="info-value">{formatDateShort(parsedDetails.CheckInDate)}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Check-Out</span>
                  <span className="info-value">{formatDateShort(parsedDetails.CheckOutDate)}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Nights</span>
                  <span className="info-value">
                    {parsedDetails.CheckInDate && parsedDetails.CheckOutDate
                      ? Math.ceil((new Date(parsedDetails.CheckOutDate).getTime() - new Date(parsedDetails.CheckInDate).getTime()) / (1000 * 60 * 60 * 24))
                      : '-'}
                  </span>
                </div>
                <div className="info-item full-width">
                  <span className="info-label">Address</span>
                  <span className="info-value">{getFullAddress(parsedDetails)}</span>
                </div>
                <div className="info-item full-width">
                  <span className="info-label">User</span>
                  <span className="info-value">{modalData.first_name} {modalData.last_name} ({modalData.email})</span>
                </div>
              </div>
            </div>

            {/* Guests */}
            {parsedDetails.Rooms && parsedDetails.Rooms.length > 0 && (
              <div className="detail-section">
                <h3 className="section-title">Guests</h3>
                <div className="passengers-grid">
                  {parsedDetails.Rooms.flatMap((room, roomIdx) => 
                    room.HotelPassenger.map((guest, guestIdx) => (
                      <div key={`${roomIdx}-${guestIdx}`} className="passenger-card">
                        <div className="passenger-avatar">
                          {guest.FirstName?.charAt(0)}{guest.LastName?.charAt(0)}
                        </div>
                        <div className="passenger-details">
                          <div className="passenger-name">
                            {guest.Title} {guest.FirstName} {guest.LastName}
                            {guest.LeadPassenger && <span className="lead-badge"> (Lead)</span>}
                          </div>
                          <div className="passenger-email">{guest.Email}</div>
                          {guest.Phoneno && <div className="passenger-contact">📞 {guest.Phoneno}</div>}
                          {guest.Age && <div className="passenger-age">Age: {guest.Age}</div>}
                          <div className="passenger-room">Room {roomIdx + 1}</div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Rooms */}
            {parsedDetails.Rooms && parsedDetails.Rooms.length > 0 && (
              <div className="detail-section">
                <h3 className="section-title">Rooms</h3>
                <table className="tickets-table">
                  <thead>
                    <tr>
                      <th>Room Type</th>
                      <th>Adults</th>
                      <th>Children</th>
                      <th>Rate (per night)</th>
                      <th>Inclusions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedDetails.Rooms.map((room, idx) => (
                      <tr key={idx}>
                        <td>{room.RoomTypeName || '-'}</td>
                        <td>{room.AdultCount || '-'}</td>
                        <td>{room.ChildCount || '-'}</td>
                        <td>
                          {room.PriceBreakUp?.RoomRate 
                            ? `${room.PriceBreakUp.CurrencyCode || 'INR'} ${room.PriceBreakUp.RoomRate.toLocaleString()}`
                            : '-'}
                        </td>
                        <td>{room.Inclusion || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Price Breakdown */}
            <div className="detail-section">
              <h3 className="section-title">Price Breakdown</h3>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Room Rate</span>
                  <span className="info-value price">
                    {parsedDetails.CurrencyCode || 'INR'} {parsedDetails.NetAmount ? (parsedDetails.NetAmount - (parsedDetails.NetTax || 0)).toFixed(2) : '-'}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Tax</span>
                  <span className="info-value price">
                    {parsedDetails.CurrencyCode || 'INR'} {parsedDetails.NetTax?.toFixed(2) || '0.00'}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Total Amount</span>
                  <span className="info-value price">
                    {parsedDetails.CurrencyCode || 'INR'} {parsedDetails.NetAmount?.toFixed(2) || modalData.total_price}
                  </span>
                </div>
                {parsedDetails.Rooms?.[0]?.PriceBreakUp?.RoomTax && (
                  <div className="info-item">
                    <span className="info-label">Room Tax</span>
                    <span className="info-value price">
                      {parsedDetails.Rooms[0].PriceBreakUp.CurrencyCode || 'INR'} {parsedDetails.Rooms[0].PriceBreakUp.RoomTax?.toFixed(2)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Cancellation Policy */}
            <div className="detail-section">
              <h3 className="section-title">Cancellation Policy</h3>
              <div className="cancellation-policy-box">
                {getCancellationPolicy(parsedDetails)}
              </div>
            </div>

            {/* Rate Conditions (Important Info) */}
            {parsedDetails.RateConditions && parsedDetails.RateConditions.length > 0 && (
              <div className="detail-section">
                <h3 className="section-title">Important Information</h3>
                <div className="cancellation-policy-box">
                  <ul style={{ margin: 0, paddingLeft: '20px' }}>
                    {parsedDetails.RateConditions.map((cond, idx) => (
                      <li key={idx} dangerouslySetInnerHTML={{ __html: cond.replace(/<[^>]*>/g, '') }} />
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        ) : modalData ? (
          <div className="flight-detail-modal">
            <p>Parsing details failed. Raw data:</p>
            <pre>{JSON.stringify(modalData, null, 2)}</pre>
          </div>
        ) : (
          <div className="no-data">No details available</div>
        )}
      </DetailModal>
    </div>
  );
};

export default HotelBookings;